/**
 * <atb-bundle-widget>
 * --------------------------------------------------------------------------
 *
 * ⚠️ STYLING-SYNC NOTE — Variant picker modal
 * ----------------------------------------------------------------------
 * This widget's variant picker (_openVariantModal + _renderModalOptions
 * below, with `.atb-bundle-widget__modal*` classes in atb-bundle-widget.css)
 * has a SIBLING DUPLICATE on the favorites page:
 *   - ATB-OS: public/atb-favorites.js  → openVariantPicker, ensurePickerModal
 *   - theme:  theme/assets/atb-favorites-page.css  (scoped `.atb-favorites-cart-modal*`)
 *
 * Both pickers were intentionally kept isolated rather than extracted
 * into a shared module. If you retune option-button visuals, modal
 * layout, sold-out states, or price typography here, propagate the
 * same change to the favorites-side picker or they will drift over
 * time. (Long-term: extract into a shared snippet+JS module — filed
 * as a future refactor.)
 * ----------------------------------------------------------------------
 *
 * "Buy together and save" PDP bundle widget.
 *
 * - Seeds on the current product (data-product-id, the anchor).
 * - Fetches /recommendations/products.json with intent=complementary
 *   to find bundle partners.
 * - Renders anchor card (server-side) + N complementary cards (JS).
 * - Single combined "Add bundle to cart" CTA POSTs all items in one
 *   /cart/add.js call. Discount (configured as an Automatic Discount
 *   in Shopify Admin) applies at checkout.
 *
 * Hides the entire section if no complementary products are configured
 * for the anchor. Architecture and helpers mirror atb-pdp-upsell.js.
 */

class AtbBundleWidget extends HTMLElement {
  constructor() {
    super();

    this.row = this.querySelector('.atb-bundle-widget__row');
    this.summary = this.querySelector('.atb-bundle-widget__summary');
    this.summarySkeleton = this.querySelector('[data-summary-skeleton]');
    this.summaryReal = this.querySelector('[data-summary-real]');
    this.template = this.querySelector('.atb-bundle-widget__card-template');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.originalEl = this.querySelector('[data-bundle-original]');
    this.savingsEl = this.querySelector('[data-bundle-savings]');
    this.ctaButton = this.querySelector('[data-bundle-cta]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.anchorPriceDisplay = this.querySelector('[data-anchor-price-display]');
    this.anchorCard = this.querySelector('[data-anchor-card]');
    this.anchorImageEl = this.querySelector('[data-anchor-image-el]');
    this.anchorVariantTrigger = this.querySelector('[data-anchor-variant-trigger]');
    this.anchorVariantLabel = this.querySelector('[data-anchor-variant-label]');

    // Anchor product JSON (full variants + options) embedded in the
    // section liquid. Powers the variant picker modal for the anchor
    // card so customers can change the PDP variant from inside the
    // bundle UI; modal confirms publish PUB_SUB_EVENTS.variantChange
    // so Dawn's main PDP picker stays in sync.
    this.anchorProduct = null;
    const anchorJsonEl = this.querySelector('script[type="application/json"][data-anchor-product]');
    if (anchorJsonEl) {
      try {
        this.anchorProduct = JSON.parse(anchorJsonEl.textContent);
      } catch (e) {
        this.anchorProduct = null;
      }
    }

    // Variant picker modal (shared across all multi-variant cards in
    // this section). Created in the section liquid; populated by JS
    // on demand when a customer clicks a card's variant trigger.
    this.modal = this.querySelector('[data-variant-modal]');
    this.modalTitle = this.querySelector('[data-variant-modal-title]');
    this.modalSubtitle = this.querySelector('[data-variant-modal-subtitle]');
    this.modalOptions = this.querySelector('[data-variant-modal-options]');
    this.modalPrice = this.querySelector('[data-variant-modal-price]');
    this.modalConfirm = this.querySelector('[data-variant-modal-confirm]');

    // Per-card selected variant tracking. Keyed by product id; value is
    // the active variant object. Initially populated to the first
    // available variant of each complementary product (matches old
    // behavior); updated when customer picks via the modal.
    this.cardSelections = new Map();

    this.productId = this.dataset.productId;
    this.anchorVariantId = this.dataset.anchorVariantId;
    this.anchorPrice = parseInt(this.dataset.anchorPrice, 10) || 0;
    this.anchorTitle = this.dataset.anchorTitle || '';
    this.anchorUrl = this.dataset.anchorUrl || '#';
    this.anchorImage = this.dataset.anchorImage || '';
    this.limit = parseInt(this.dataset.limit, 10) || 2;
    this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
    this.discountText = this.dataset.discountText || '';
    // Shopify discount code applied on Add Bundle (via /discount/<code>
    // session URL). Empty = no code applied (relies on whatever
    // automatic discount is configured in Shopify Admin, if any).
    this.discountCode = this.dataset.discountCode || '';

    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.complementaryProducts = []; // populated after fetch
    this._loaded = false;
    this._adding = false;
  }

  connectedCallback() {
    if (!this.productId || !this.anchorVariantId || !this.row || !this.template) {
      this.remove();
      return;
    }

    // Reformat the anchor card's server-rendered prices via JS so the
    // currency prefix matches the complementary cards. Server uses
    // Liquid `| money` (shop's default `money_format`, e.g. "$38.70"
    // regardless of market), client uses Intl.NumberFormat with the
    // active currency (e.g. "CA$38.70" in CAD market). Overwriting on
    // connection unifies both to the JS format — values are functionally
    // identical, only the currency prefix differs.
    if (this.anchorPriceDisplay) {
      this._setCardPrice(this.anchorPriceDisplay, this.anchorPrice);
    }

    // Lazy-load on intersection (400px margin) — same as atb-pdp-upsell.
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._observer.disconnect();
              this.load();
            }
          });
        },
        { rootMargin: '400px 0px' }
      );
      this._observer.observe(this);
    } else {
      this.load();
    }

    // Wire up the Add bundle button (may be clicked before fetch completes
    // if Safari is fast — guard inside the handler).
    if (this.ctaButton) {
      this.ctaButton.addEventListener('click', () => this.addBundle());
    }

    // Listen for variant changes on the parent product page. Dawn publishes
    // PUB_SUB_EVENTS.variantChange when the variant picker updates.
    // PUB_SUB_EVENTS is a top-level const in Dawn's constants.js: a global, but not on window.
    if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsubscribeVariant = window.subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => this._onVariantChange(event)
      );
    }

    // If the anchor product is multi-variant, expose the picker trigger
    // on the anchor card and wire it to open the shared variant modal.
    // Single-variant anchors keep the trigger hidden (Liquid default).
    if (this.anchorProduct
        && Array.isArray(this.anchorProduct.variants)
        && this.anchorProduct.variants.length > 1
        && this.anchorVariantTrigger) {
      this.anchorVariantTrigger.hidden = false;
      // Initial label reflects whatever variant the PDP boot-up resolved
      // (matches `this.anchorVariantId` set from the data attribute).
      const initialVariant =
        this.anchorProduct.variants.find((v) => String(v.id) === String(this.anchorVariantId))
        || this.anchorProduct.variants.find((v) => v.available)
        || this.anchorProduct.variants[0];
      if (initialVariant && this.anchorVariantLabel) {
        this.anchorVariantLabel.textContent = initialVariant.title || 'Choose options';
      }
      this.anchorVariantTrigger.addEventListener('click', () => {
        this._openVariantModal(this.anchorProduct);
      });
    }

    // Modal: close on backdrop click, close button, or Escape.
    if (this.modal) {
      this.modal.querySelectorAll('[data-variant-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this._closeModal());
      });
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && !this.modal.hidden) this._closeModal();
      };
      document.addEventListener('keydown', this._onKeydown);

      // Confirm button finalizes the pending selection.
      if (this.modalConfirm) {
        this.modalConfirm.addEventListener('click', () => this._confirmModalSelection());
      }
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (typeof this._unsubscribeVariant === 'function') this._unsubscribeVariant();
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    document.body.classList.remove('atb-bundle-widget-modal-open');
    // Defensive: if the widget tears down with the modal open, restore
    // body scroll so the user isn't left with a frozen page.
    this._unlockBodyScrollIOS();
  }

  _onVariantChange(event) {
    const variant = event && event.data && event.data.variant;
    if (!variant) return;
    // Quick-add modals publish variantChange too; only follow this product's own variants.
    const own = this.anchorProduct && Array.isArray(this.anchorProduct.variants) ? this.anchorProduct.variants : null;
    if (own && !own.some((v) => String(v.id) === String(variant.id))) return;
    this._updateAnchorCard(variant);
  }

  /**
   * Apply a variant change to the anchor card. Called from two paths:
   * 1) Dawn's PDP variant picker → PUB_SUB_EVENTS.variantChange → _onVariantChange
   * 2) The bundle's own variant modal confirming a pick on the anchor product
   *
   * Updates: price (existing behavior), image, variant title label,
   * availability state (disables the Add Bundle CTA if the new variant
   * is sold out). Idempotent — re-running with the same variant is a no-op.
   */
  _updateAnchorCard(variant) {
    if (!variant) return;
    this.anchorVariantId = String(variant.id);
    this.anchorPrice = typeof variant.price === 'number'
      ? variant.price
      : parseInt(variant.price, 10) || this.anchorPrice;

    if (this.anchorPriceDisplay) {
      this._setCardPrice(this.anchorPriceDisplay, this.anchorPrice);
    }

    if (this.anchorVariantLabel && variant.title) {
      this.anchorVariantLabel.textContent = variant.title;
    }

    // Variant image swap. Uses shared helper so anchor + complementary
    // cards stay consistent. Falls back to product-level featured image
    // when the variant has no image of its own.
    this._applyVariantImage(this.anchorImageEl, variant, this.anchorProduct);

    // Availability gate. If the customer switches to a sold-out variant
    // (via the PDP picker or our own modal), disable the bundle CTA so
    // they can't POST an invalid line item, and show a sentinel error.
    // Re-enable when an available variant is re-selected.
    const available = variant.available !== false;
    if (this.ctaButton) {
      this.ctaButton.disabled = !available;
    }
    if (this.anchorCard) {
      this.anchorCard.classList.toggle('is-unavailable', !available);
    }
    if (this.errorEl) {
      const sentinel = 'Selected variant is unavailable';
      if (!available) {
        this.errorEl.textContent = sentinel;
        this.errorEl.hidden = false;
      } else if (this.errorEl.textContent === sentinel) {
        // Only clear OUR error; don't clobber a real cart error.
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    this._recomputeTotals();
  }

  /**
   * Best-effort sync of Dawn's PDP variant picker (radio / select inputs)
   * when the customer changes the anchor variant via our bundle modal.
   * Without this, the picker on the page above the bundle would stay
   * visually pinned to the old variant even though everything else
   * (URL, bundle state, cart adds) reflects the new one.
   *
   * Strategy: locate Dawn's <variant-radios> or <variant-selects> custom
   * element, find inputs whose names match each option dimension, check
   * the right value and dispatch `change` so Dawn's existing onChange
   * handler runs (which will publish PUB_SUB_EVENTS.variantChange back
   * — idempotent for us since _updateAnchorCard short-circuits on no
   * change).
   *
   * Fragile to theme markup — best-effort only. If selectors miss, the
   * bundle widget still works; the PDP picker just stays out of sync.
   */
  _syncPdpVariantPicker(variant) {
    if (!variant || !this.anchorProduct) return;
    const picker = document.querySelector('variant-radios, variant-selects');
    if (!picker) return;

    (this.anchorProduct.options || []).forEach((option, idx) => {
      const optionName = typeof option === 'string' ? option : (option && option.name);
      const value = variant[`option${idx + 1}`];
      if (!optionName || !value) return;

      // Dawn pickers use inputs named like `Size` or `options[Size]`.
      const inputs = picker.querySelectorAll(
        `input[name="${optionName}"], input[name="options[${optionName}]"], select[name="${optionName}"], select[name="options[${optionName}]"]`
      );
      inputs.forEach((input) => {
        if (input.tagName === 'SELECT') {
          if (input.value !== value) {
            input.value = value;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (input.type === 'radio') {
          if (input.value === value && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  async load() {
    if (this._loaded) return;
    this._loaded = true;

    try {
      // Request a larger candidate pool than we need. Shopify returns the
      // `complementary_products` metafield list in the order set by the
      // merchant; the JS filter below drops sold-out products and then
      // slices to `this.limit`. So if positions 1-2 are sold out, positions
      // 3-5 become natural fallbacks instead of the widget hiding entirely.
      const poolSize = Math.max(this.limit, 5);
      const recsUrl = `${this.routeRoot}recommendations/products.json?product_id=${this.productId}&limit=${poolSize}&intent=complementary`;
      const sectionUrl = `${this.routeRoot}recommendations/products?product_id=${this.productId}&limit=${poolSize}&intent=complementary&section_id=upsell-data`;

      const [res, sectionRes] = await Promise.all([
        fetch(recsUrl, { credentials: 'same-origin' }),
        fetch(sectionUrl, { credentials: 'same-origin' }).catch(() => null),
      ]);

      if (!res.ok) {
        this.remove();
        return;
      }
      const data = await res.json();

      // Parse short-title metafield map (same pattern as atb-pdp-upsell.js).
      this.shortTitleMap = {};
      if (sectionRes && sectionRes.ok) {
        try {
          const html = await sectionRes.text();
          const match = html.match(/<script[^>]*data-upsell-meta[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            JSON.parse(match[1]).forEach(({ handle, short_title }) => {
              if (handle && short_title) this.shortTitleMap[handle] = short_title;
            });
          }
        } catch (e) {
          /* fall back to product.title */
        }
      }

      // Keep only products that have at least one available variant.
      const available = (data.products || [])
        .filter((p) => {
          const variants = p.variants || [];
          return variants.some((v) => v.available);
        })
        .slice(0, this.limit);

      // Minimum bundle size = 3 (anchor + 2 complementaries) so every
      // rendered bundle qualifies for the RVSCG8 discount's 3-item
      // minimum requirement. If filtering for availability leaves <2
      // complementaries, hide the widget rather than promise a "Save
      // 10%" badge that checkout won't actually honor.
      if (available.length < 2) {
        this.remove();
        return;
      }

      this.complementaryProducts = available;
      this.render(available);
      this._recomputeTotals();
      this._revealSummary();
    } catch (err) {
      console.warn('[atb-bundle-widget] load failed:', err);
      this.remove();
    }
  }

  render(products) {
    // Remove skeleton placeholders.
    this.row.querySelectorAll('.atb-bundle-widget__card--skeleton').forEach((el) => el.remove());

    products.forEach((product) => {
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.productId = product.id;

      const productUrl = product.url || `${this.routeRoot}products/${product.handle}`;
      const mediaLink = card.querySelector('.atb-bundle-widget__media');
      const img = card.querySelector('.atb-bundle-widget__image');
      const titleLink = card.querySelector('.atb-bundle-widget__title');
      const priceEl = card.querySelector('.atb-bundle-widget__price');
      const variantTrigger = card.querySelector('[data-variant-trigger]');
      const variantLabel = card.querySelector('[data-variant-label]');

      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-hidden', 'false');
      mediaLink.removeAttribute('tabindex');
      mediaLink.setAttribute('aria-label', product.title);

      titleLink.href = productUrl;
      titleLink.textContent =
        (this.shortTitleMap && this.shortTitleMap[product.handle]) || product.title;

      const imgSrc = this._extractImageSrc(product);
      if (imgSrc) {
        img.src = this._sizedImage(imgSrc, '600x600');
        img.alt =
          product.featured_image && product.featured_image.alt
            ? product.featured_image.alt
            : product.title;
      } else {
        img.remove();
      }

      // Pick an initial variant — first available variant. Stored so the
      // customer can change it via the variant-picker modal.
      const firstAvailable = (product.variants || []).find((v) => v.available)
                          || (product.variants || [])[0];
      this.cardSelections.set(String(product.id), firstAvailable);

      // Use the selected variant's price for display + bundle math.
      const priceForCard = firstAvailable
        ? (typeof firstAvailable.price === 'number'
           ? firstAvailable.price
           : parseInt(firstAvailable.price, 10) || product.price)
        : product.price;
      this._setCardPrice(priceEl, priceForCard);

      // If product is multi-variant, surface the picker trigger and
      // show the currently selected variant's title (e.g. "S / Red").
      if (variantTrigger && product.variants && product.variants.length > 1) {
        variantTrigger.hidden = false;
        if (variantLabel) {
          variantLabel.textContent = (firstAvailable && firstAvailable.title) || 'Choose options';
        }
        variantTrigger.addEventListener('click', () => this._openVariantModal(product));
      }

      this.row.appendChild(card);
    });
  }

  /**
   * Open the variant picker modal for a given product.
   * Populates one row of pill buttons per option dimension (Size, Color,
   * etc.). Sold-out option values render as disabled. Customer picks a
   * value from each row; "Confirm" finalizes the selection.
   */
  /*
   * iOS Safari–friendly body scroll lock. Plain `overflow:hidden` on
   * body doesn't actually prevent touch scroll on iOS — the variant
   * modal appeared to "create empty space at the bottom" because the
   * page was scrolling under it (URL bar show/hide + scroll chaining
   * from modal's internal overflow:auto). Take the body OUT of document
   * flow, pin to its current scroll Y, restore on close. Tracked via
   * body dataset so a double-open doesn't lose the original scroll
   * position. Mirrors the same helper in atb-favorites.js (sibling
   * picker on the favorites page).
   */
  _lockBodyScrollIOS() {
    if (document.body.dataset.atbScrollLockY !== undefined) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.atbScrollLockY = String(scrollY);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  _unlockBodyScrollIOS() {
    const saved = document.body.dataset.atbScrollLockY;
    if (saved === undefined) return;
    const scrollY = parseInt(saved, 10) || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    delete document.body.dataset.atbScrollLockY;
    window.scrollTo(0, scrollY);
  }

  _openVariantModal(product) {
    if (!this.modal) return;

    this._modalProduct = product;
    // Seed pending selection from the currently active variant so
    // canceling (X / backdrop / Escape) leaves card state unchanged.
    // Anchor product: look up variant by anchorVariantId (cardSelections
    // is keyed only for complementary cards).
    const isAnchor = this.anchorProduct
                     && String(product.id) === String(this.anchorProduct.id);
    let current;
    if (isAnchor) {
      current = (product.variants || []).find(
        (v) => String(v.id) === String(this.anchorVariantId)
      );
    } else {
      current = this.cardSelections.get(String(product.id));
    }
    this._pendingSelection = {};
    (product.options || []).forEach((_optionName, optionIndex) => {
      const key = `option${optionIndex + 1}`;
      this._pendingSelection[optionIndex] = current ? current[key] : null;
    });

    if (this.modalTitle) this.modalTitle.textContent = product.title;
    if (this.modalSubtitle) {
      this.modalSubtitle.textContent =
        (product.options && product.options.length > 1)
          ? 'Choose your options'
          : 'Choose your option';
    }

    this._renderModalOptions();
    this._renderModalFooter();

    this.modal.hidden = false;
    document.body.classList.add('atb-bundle-widget-modal-open');
    this._lockBodyScrollIOS();
  }

  _renderModalOptions() {
    if (!this.modalOptions) return;
    this.modalOptions.innerHTML = '';
    const product = this._modalProduct;
    if (!product) return;

    (product.options || []).forEach((option, optionIndex) => {
      // Shopify's `/recommendations/products.json` returns `options` as an
      // array of OBJECTS (`{name, position, values}`). Some endpoints
      // (storefront API) return a flat array of strings. Handle both —
      // pull `.name` when it's an object, use the value directly when
      // it's a string. Default fallback: "Option 1", "Option 2", etc.
      const optionName =
        (typeof option === 'string' && option)
        || (option && option.name)
        || `Option ${optionIndex + 1}`;

      const row = document.createElement('div');
      row.className = 'atb-bundle-widget__option-row';

      const label = document.createElement('p');
      label.className = 'atb-bundle-widget__option-label';
      label.textContent = optionName;
      row.appendChild(label);

      const values = document.createElement('div');
      values.className = 'atb-bundle-widget__option-values';
      values.setAttribute('role', 'radiogroup');
      values.setAttribute('aria-label', optionName);

      this._uniqueOptionValues(product, optionIndex).forEach((value) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'atb-bundle-widget__option-value';
        btn.textContent = value;
        btn.setAttribute('role', 'radio');

        if (this._pendingSelection[optionIndex] === value) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');
        } else {
          btn.setAttribute('aria-checked', 'false');
        }

        if (!this._isOptionValueAvailable(product, optionIndex, value)) {
          btn.disabled = true;
          btn.classList.add('is-disabled');
          btn.title = 'Sold out';
        }

        btn.addEventListener('click', () => {
          this._pendingSelection[optionIndex] = value;
          // Toggle existing button class state instead of rebuilding
          // the whole options subtree — the rebuild caused a visible
          // flash because the browser tore down nodes and re-ran
          // transitions on freshly-created elements. Sister picker on
          // the favorites page got the same fix.
          this._updateModalSelectionUI();
          this._renderModalFooter();
        });

        values.appendChild(btn);
      });

      row.appendChild(values);
      this.modalOptions.appendChild(row);
    });
  }

  /**
   * Updates is-selected + disabled/is-disabled state on the EXISTING
   * option buttons rendered by _renderModalOptions. Called on every
   * click instead of re-running _renderModalOptions, which used to
   * rebuild the entire options DOM and cause a visible flash. Sold-
   * out evaluation still re-runs per click because the available
   * variants depend on the current selection.
   */
  _updateModalSelectionUI() {
    if (!this.modalOptions || !this._modalProduct) return;
    const product = this._modalProduct;
    const rows = this.modalOptions.querySelectorAll('.atb-bundle-widget__option-row');
    rows.forEach((row, optionIndex) => {
      const buttons = row.querySelectorAll('.atb-bundle-widget__option-value');
      buttons.forEach((btn) => {
        const value = btn.textContent;
        const isSelected = this._pendingSelection[optionIndex] === value;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        const available = this._isOptionValueAvailable(product, optionIndex, value);
        btn.disabled = !available;
        btn.classList.toggle('is-disabled', !available);
        if (!available) {
          btn.title = 'Sold out';
        } else {
          btn.removeAttribute('title');
        }
      });
    });
  }

  _renderModalFooter() {
    const variant = this._findPendingVariant();
    if (this.modalPrice) {
      if (variant) {
        // Reuse the same helper that drives bundle card prices so the
        // modal shows discounted + struck-through original identically.
        const raw = typeof variant.price === 'number'
          ? variant.price
          : parseInt(variant.price, 10) || 0;
        this._setCardPrice(this.modalPrice, raw);
      } else {
        // Clear both spans when no variant is selected.
        const discounted = this.modalPrice.querySelector('.atb-bundle-widget__price-discounted');
        const original = this.modalPrice.querySelector('.atb-bundle-widget__price-original');
        if (discounted) discounted.textContent = '';
        if (original) original.textContent = '';
      }
    }
    if (this.modalConfirm) {
      const valid = !!(variant && variant.available);
      this.modalConfirm.disabled = !valid;
      this.modalConfirm.textContent = valid ? 'Confirm' : 'Sold out';
    }
  }

  /**
   * Resolve which variant the current pending selection points to.
   * Matches all selected option values against `option1/option2/option3`
   * on each variant. Returns null if no combination is fully selected
   * or no matching variant exists.
   */
  _findPendingVariant() {
    const product = this._modalProduct;
    if (!product) return null;
    return (product.variants || []).find((v) => {
      for (let i = 0; i < (product.options || []).length; i++) {
        const expected = this._pendingSelection[i];
        if (!expected) return false;
        if (v[`option${i + 1}`] !== expected) return false;
      }
      return true;
    }) || null;
  }

  _uniqueOptionValues(product, optionIndex) {
    const seen = new Set();
    const order = [];
    (product.variants || []).forEach((v) => {
      const value = v[`option${optionIndex + 1}`];
      if (value && !seen.has(value)) {
        seen.add(value);
        order.push(value);
      }
    });
    return order;
  }

  /**
   * Smart availability: a value at optionIndex is available if there's an
   * available variant where that option equals `value` AND every other
   * selected option also matches. Lets us gray out e.g. Color: Red when
   * the customer has Size: M selected and only S/Red is in stock.
   */
  _isOptionValueAvailable(product, optionIndex, value) {
    return (product.variants || []).some((v) => {
      if (!v.available) return false;
      if (v[`option${optionIndex + 1}`] !== value) return false;
      for (let i = 0; i < (product.options || []).length; i++) {
        if (i === optionIndex) continue;
        const expected = this._pendingSelection[i];
        if (expected && v[`option${i + 1}`] !== expected) return false;
      }
      return true;
    });
  }

  _confirmModalSelection() {
    const variant = this._findPendingVariant();
    if (!variant || !variant.available) return;

    const product = this._modalProduct;
    if (!product) {
      this._closeModal();
      return;
    }

    const isAnchor = this.anchorProduct
                     && String(product.id) === String(this.anchorProduct.id);

    if (isAnchor) {
      // Anchor variant change from inside the bundle modal. Update our
      // own card state, then best-effort sync Dawn's PDP variant picker
      // above so the customer sees the new selection reflected
      // everywhere (radios / select dropdown, URL via Dawn's own
      // onChange handler).
      this._updateAnchorCard(variant);
      this._syncPdpVariantPicker(variant);
    } else {
      // Complementary card variant change.
      this.cardSelections.set(String(product.id), variant);
      const card = this.row.querySelector(`[data-product-id="${product.id}"]`);
      if (card) {
        const label = card.querySelector('[data-variant-label]');
        if (label) label.textContent = variant.title || 'Choose options';
        const priceEl = card.querySelector('.atb-bundle-widget__price');
        if (priceEl) {
          const variantPrice = typeof variant.price === 'number'
            ? variant.price
            : parseInt(variant.price, 10) || 0;
          this._setCardPrice(priceEl, variantPrice);
        }
        // Swap the card image to the selected variant's featured image
        // if one exists. Products with color/style variants typically
        // have per-variant featured images set in admin; without this
        // the card kept showing the original product image after a
        // variant pick.
        const imgEl = card.querySelector('.atb-bundle-widget__image');
        this._applyVariantImage(imgEl, variant, product);
      }
      this._recomputeTotals();
    }

    this._closeModal();
  }

  _closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('atb-bundle-widget-modal-open');
    this._unlockBodyScrollIOS();
    this._modalProduct = null;
    this._pendingSelection = null;
  }

  /**
   * Populate a card's price block (either anchor or complementary) with
   * the discounted amount + struck-through original. When discountPercent
   * is 0, only the discounted span is filled (which equals the original
   * price), and the strikethrough stays empty (hidden via CSS `:empty`).
   *
   * Uses the same `Math.round(price * (100 - discount) / 100)` formula as
   * the Liquid server-side render for the anchor, so totals stay consistent
   * to the cent across SSR + JS updates.
   */
  /**
   * Swap a card's <img> to a variant's featured image. Handles both
   * data shapes Shopify exposes:
   *   Liquid `product | json`           → variant.featured_image is a
   *                                       STRING URL
   *   /recommendations/products.json    → variant.featured_image is an
   *                                       OBJECT { src, alt }
   *
   * If the variant has no image, falls back to the product's
   * featured_image so picking a different variant of a product with
   * shared imagery doesn't accidentally blank the card.
   * Idempotent — re-running with the same variant is a no-op DOM write
   * because the src compare short-circuits.
   */
  _applyVariantImage(imgEl, variant, productFallback) {
    if (!imgEl || !variant) return;

    let rawSrc = null;
    let alt = null;

    if (variant.featured_image) {
      if (typeof variant.featured_image === 'string') {
        rawSrc = variant.featured_image;
      } else {
        rawSrc = variant.featured_image.src || null;
        alt = variant.featured_image.alt || null;
      }
    }

    // Fall back to product-level featured_image so a variant without
    // its own image still resets the card from a previously-shown
    // variant image (rather than getting stuck on the wrong picture).
    if (!rawSrc && productFallback && productFallback.featured_image) {
      if (typeof productFallback.featured_image === 'string') {
        rawSrc = productFallback.featured_image;
      } else {
        rawSrc = productFallback.featured_image.src || null;
        alt = alt || productFallback.featured_image.alt || null;
      }
    }

    if (!rawSrc) return;

    imgEl.src = this._sizedImage(rawSrc, '600x600');
    if (alt) imgEl.alt = alt;
  }

  _setCardPrice(priceEl, originalPrice) {
    if (!priceEl) return;
    const discountedEl = priceEl.querySelector('.atb-bundle-widget__price-discounted');
    const originalEl = priceEl.querySelector('.atb-bundle-widget__price-original');
    if (!discountedEl) return;

    if (this.discountPercent > 0) {
      const discounted = Math.round((originalPrice * (100 - this.discountPercent)) / 100);
      discountedEl.textContent = this._formatMoney(discounted);
      if (originalEl) originalEl.textContent = this._formatMoney(originalPrice);
    } else {
      discountedEl.textContent = this._formatMoney(originalPrice);
      if (originalEl) originalEl.textContent = '';
    }
  }

  _recomputeTotals() {
    if (this.complementaryProducts.length === 0) return;

    // Sum the SELECTED variant's price per card (not product.price), so
    // variant pricing (e.g. XL costs more than M) is reflected in the
    // bundle total and savings math. Falls back to product.price if a
    // card doesn't have a selection yet (shouldn't happen — render()
    // seeds cardSelections — but defensive).
    const complementaryTotal = this.complementaryProducts.reduce((sum, p) => {
      const selected = this.cardSelections.get(String(p.id));
      const raw = selected
        ? (typeof selected.price === 'number' ? selected.price : parseInt(selected.price, 10))
        : (typeof p.price === 'number' ? p.price : parseInt(p.price, 10));
      return sum + (Number.isFinite(raw) ? raw : 0);
    }, 0);
    const subtotal = this.anchorPrice + complementaryTotal;
    const discounted = Math.round(subtotal * (1 - this.discountPercent / 100));
    const savings = subtotal - discounted;

    if (this.totalEl) this.totalEl.textContent = this._formatMoney(discounted);
    if (this.originalEl) {
      if (this.discountPercent > 0 && savings > 0) {
        this.originalEl.textContent = this._formatMoney(subtotal);
      } else {
        this.originalEl.textContent = '';
      }
    }
    if (this.savingsEl) {
      if (this.discountPercent > 0 && savings > 0) {
        this.savingsEl.textContent = `Save ${this._formatMoney(savings)}`;
      } else {
        this.savingsEl.textContent = '';
      }
    }
  }

  _revealSummary() {
    // Swap the skeleton out for the real content. The aside itself
    // stays visible the whole time so the right column reserves its
    // final height from first paint (no layout jump on data arrival).
    if (this.summarySkeleton) this.summarySkeleton.hidden = true;
    if (this.summaryReal) this.summaryReal.hidden = false;
  }

  async addBundle() {
    if (this._adding) return;
    if (!this.anchorVariantId || this.complementaryProducts.length === 0) return;

    this._adding = true;
    this._setLoading(true);
    this._clearError();

    try {
      // Build line-items list: anchor first, then each complementary
      // (using the per-card selected variant — set by the picker modal,
      // or defaulting to first available at render time).
      const items = [{ id: parseInt(this.anchorVariantId, 10), quantity: 1 }];

      this.complementaryProducts.forEach((product) => {
        const selected = this.cardSelections.get(String(product.id));
        // Fall back to first available variant if no explicit selection
        // (defensive — should always be set after render()).
        const variantToAdd = (selected && selected.available)
          ? selected
          : (product.variants || []).find((v) => v.available);
        if (variantToAdd) {
          items.push({ id: parseInt(variantToAdd.id, 10), quantity: 1 });
        }
      });

      const sections = this._sectionsToRender();
      const body = JSON.stringify({
        items,
        sections: sections.map((s) => s.section).join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch(`${this.routeRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });
      const data = await res.json();

      if (data.status) {
        this._showError(data.description || data.message || 'Could not add bundle.');
        return;
      }

      // If a discount code is configured, apply it via Shopify's
      // /discount/<code> session URL (the documented client-side way
      // to apply a code without a page reload), then refetch cart
      // sections so the drawer renders with the discount line.
      // If no code is set, this is a no-op — the existing `data`
      // from /cart/add.js is used directly.
      const finalState = await this._maybeApplyDiscountCode(data);

      this._updateCartDrawer(finalState);
      this._publishCartUpdate(this.anchorVariantId, finalState);
    } catch (err) {
      console.warn('[atb-bundle-widget] addBundle failed:', err);
      this._showError('Something went wrong. Please try again.');
    } finally {
      this._adding = false;
      this._setLoading(false);
    }
  }

  _setLoading(loading) {
    if (!this.ctaButton) return;
    this.ctaButton.disabled = loading;
    const textSpan = this.ctaButton.querySelector('.atb-bundle-widget__cta-text');
    const loadingSpan = this.ctaButton.querySelector('.atb-bundle-widget__cta-loading');
    if (textSpan) textSpan.hidden = loading;
    if (loadingSpan) loadingSpan.hidden = !loading;
  }

  _showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  _clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  /**
   * Apply the section's discount code with "customer-arrived" preservation.
   *
   * Two-part safety:
   * 1. If the customer arrived via a different discount link (e.g. a
   *    referral) and that code is incompatible with our bundle code,
   *    Shopify silently *replaces* the existing code when we apply
   *    ours. To prevent silently overriding customer-arrived incentives,
   *    we snapshot existing codes before applying, then check what's
   *    in the cart after. Any code that was removed gets re-applied,
   *    which kicks our bundle code back out — final state preserves
   *    whatever the customer arrived with.
   * 2. If the existing code IS compatible (different type +
   *    `combinesWith` allows our type), Shopify lets them stack. The
   *    after-snapshot will contain both → nothing to restore →
   *    customer ends up with both discounts.
   *
   * So:
   *   - No existing code → bundle applied alone (normal flow)
   *   - Existing code is compatible → both stack
   *   - Existing code is incompatible → original preserved, bundle skipped
   *
   * Trade-off: in the incompatible case, customer doesn't get the
   * bundle discount. We honor the discount they ARRIVED with rather
   * than silently overriding it. The OS that generates referral codes
   * should set `combinesWith.productDiscounts = true` on those codes
   * so they stack naturally — once that ships, this restore branch
   * becomes a no-op for the common case.
   *
   * @param {object} fallbackState — the parsed state from /cart/add.js
   * @returns {Promise<object>}
   */
  async _maybeApplyDiscountCode(fallbackState) {
    if (!this.discountCode) return fallbackState;

    try {
      // 1) Snapshot existing applicable discount codes before we touch
      //    anything. `applicable !== false` filters out codes that are
      //    on the cart but aren't currently reducing the price (e.g.
      //    minimum-purchase not met) — no need to preserve those.
      const beforeCodes = await this._readAppliedDiscountCodes();

      // 2) Apply our bundle code. `/discount/<code>` returns a 302;
      //    `redirect: 'manual'` skips following it (we don't need
      //    the redirected page, just the side effect).
      await fetch(
        `${this.routeRoot}discount/${encodeURIComponent(this.discountCode)}`,
        { credentials: 'same-origin', method: 'GET', redirect: 'manual' }
      ).catch(() => { /* silent — best-effort */ });

      // 3) Snapshot again to see which codes are present after apply.
      const afterCodes = await this._readAppliedDiscountCodes();

      // 4) Any code that was applicable before but is missing now (and
      //    isn't our newly-applied bundle code) was silently replaced.
      //    Restore it by re-applying via /discount/<code>. Restoring
      //    each in turn will kick our bundle code back out (because
      //    it was incompatible with the original), so the final cart
      //    holds the customer's pre-existing codes.
      const removed = beforeCodes.filter(
        (code) => code !== this.discountCode && !afterCodes.includes(code)
      );

      if (removed.length > 0) {
        console.info(
          `[atb-bundle-widget] Bundle code "${this.discountCode}" conflicted with ${removed.length} existing code(s); restoring originals.`,
          removed
        );
        for (const code of removed) {
          await fetch(
            `${this.routeRoot}discount/${encodeURIComponent(code)}`,
            { credentials: 'same-origin', method: 'GET', redirect: 'manual' }
          ).catch(() => {});
        }
      }

      // 5) Refresh the drawer with the final cart state (whichever
      //    discounts ended up stuck).
      const sections = this._sectionsToRender();
      const refreshRes = await fetch(`${this.routeRoot}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: sections.map((s) => s.section).join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (refreshRes.ok) {
        return await refreshRes.json();
      }
    } catch (err) {
      console.warn('[atb-bundle-widget] discount apply failed:', err);
    }

    return fallbackState;
  }

  /**
   * Read currently-applicable discount codes from /cart.js. Returns
   * an array of code strings (e.g. ["RVSCG8", "REFERRAL10"]).
   * Excludes codes that are on the cart but not actively reducing
   * the price (applicable: false).
   */
  async _readAppliedDiscountCodes() {
    try {
      const res = await fetch(`${this.routeRoot}cart.js`, {
        credentials: 'same-origin',
      });
      if (!res.ok) return [];
      const cart = await res.json();
      return (cart.discount_codes || [])
        .filter((d) => d && d.applicable !== false && typeof d.code === 'string')
        .map((d) => d.code);
    } catch (_) {
      return [];
    }
  }

  // --- cart drawer helpers (same as atb-pdp-upsell.js) -------------------

  _updateCartDrawer(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      // Toggle `is-empty` on the outer <cart-drawer> BEFORE renderContents
      // (Dawn's renderContents only strips it from .drawer__inner, leaving
      // the outer class stale — caused collapsed line-items area after
      // first add from an empty cart).
      if (typeof parsedState.item_count === 'number') {
        cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
      } else {
        cartDrawer.classList.remove('is-empty');
      }
      // Let renderContents handle drawer open() via its internal setTimeout.
      // Calling open() ourselves here would collapse the animate+active
      // class additions into a single tick and skip the slide-in.
      cartDrawer.renderContents(parsedState);
      return;
    }

    // Manual fallback if cart-drawer custom element isn't found.
    this._sectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.section];
      if (!html) return;
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector(section.selector || '.shopify-section');
      if (replacement) target.innerHTML = replacement.innerHTML;
    });
  }

  _publishCartUpdate(variantId, cartData) {
    if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'atb-bundle-widget',
        productVariantId: String(variantId),
        cartData,
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartData }));
    }
  }

  _sectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  // --- generic helpers (same as atb-pdp-upsell.js) -----------------------

  _extractImageSrc(product) {
    if (!product) return null;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && (product.featured_image.url || product.featured_image.src)) {
      return product.featured_image.url || product.featured_image.src;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first.url;
    }
    return null;
  }

  _sizedImage(src, size) {
    if (!src) return '';
    let url = src.startsWith('//') ? `https:${src}` : src;
    url = url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, `_${size}$1$2`);
    return url;
  }

  _formatMoney(price) {
    const cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (!Number.isFinite(cents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {
        /* fall through */
      }
    }

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('atb-bundle-widget')) {
  customElements.define('atb-bundle-widget', AtbBundleWidget);
}
